/**
 * A Deque (Double ended queue) implemented as a non-circular, doubly linked list
 */
export class Deque<T> {
  private _front: Node<T> = null;
  private _back: Node<T> = null;
  private _numberOfNodes = 0;

  addFront(value: T) {
    if (this._front === null) {
      this._front = new Node(value);
      this._back = this._front;
    } else {
      const newNode = new Node(value);
      newNode.next = this._front;
      this._front.previous = newNode;
      this._front = newNode;
    }
    this._numberOfNodes++;
  }

  addBack(value: T) {
    if (this._back === null) {
      this._back = new Node(value);
      if (this._front === null) {
        this._front = this._back;
      }
    } else {
      const newNode = new Node(value);
      newNode.previous = this._back;
      this._back.next = newNode;
      this._back = newNode;
    }
    this._numberOfNodes++;
  }

  popFront() {
    if (this._numberOfNodes > 0) {
      const currentFront = this._front;
      this._front = currentFront.next;
      if (this._front) {
        this._front.previous = null;
      } else {
        this._back = null;
      }
      this._numberOfNodes--;
      return currentFront.value;
    }
    return null;
  }

  popBack() {
    if (this._numberOfNodes > 0) {
      const currentBack = this._back;
      this._back = currentBack.previous;
      if (this._back) {
        this._back.next = null;
      } else {
        this._front = null;
      }
      this._numberOfNodes--;
      return currentBack.value;
    }
    return null;
  }

  peekFront() {
    return this._front ? this._front.value : null;
  }

  peekBack() {
    return this._back ? this._back.value : null;
  }

  has(value: T) {
    let current = this._front;
    while (current) {
      if (current.value === value) {
        return true;
      }
      current = current.next;
    }
    return false;
  }

  peekAt(index: number) {
    if (index < this._numberOfNodes) {
      let current = this._front;
      for (let i = 0; i < index; i++) {
        current = current.next;
      }
      return current.value;
    }
    return null;
  }

  clear() {
    this._front = null;
    this._back = null;
    this._numberOfNodes = 0;
  }

  count() {
    return this._numberOfNodes;
  }
}

class Node<T> {
  previous: Node<T> = null;
  next: Node<T> = null;

  constructor(readonly value: T) {}
}
